import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { useDispatch } from "react-redux";
import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { connectWallet } from "../state/uiSlice";
import Button from "./Button";

// Helper function to shorten address
const shortenAddress = (address: string) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--background, #1a1a1a);
  border: 1px solid var(--border, #333);
  border-radius: 8px;
  padding: 0.5rem 0;
  min-width: 160px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: var(--text, #fff);
  padding: 0.75rem 1rem;
  text-align: left;
  cursor: pointer;
  font-size: 1.4rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--hover, #333);
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const AddressButton = styled.button`
  background: var(--primary, #007bff);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 1.4rem;
  padding: 1rem 1.5rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: var(--primary-hover, #0056b3);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Connector = (): JSX.Element => {
  const dispatch = useDispatch();
  const { address, isConnected } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleConnect = () => {
    if (isConnected) {
      dispatch(connectWallet());
    } else {
      // Debug: log all available connectors
      console.log('Available connectors:', connectors.map(c => ({ name: c.name, id: c.id })));
      
      // Use injected wallet connector (auto-detects Rabby, MetaMask, etc.)
      const injectedConnector = connectors.find(c => c.id === 'injected');
      const connector = injectedConnector || connectors[0];
      if (connector) {
        connect({ connector });
      }
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setDropdownOpen(false);
  };

  const handleAddressClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  if (isConnected && address) {
    return (
      <DropdownContainer ref={dropdownRef}>
        <AddressButton onClick={handleAddressClick}>
          {shortenAddress(address)}
          <span>▼</span>
        </AddressButton>
        <DropdownMenu isOpen={dropdownOpen}>
          <DropdownItem onClick={handleDisconnect}>
            Disconnect Wallet
          </DropdownItem>
        </DropdownMenu>
      </DropdownContainer>
    );
  }

  return (
    <Button click={handleConnect}>
      Connect Wallet
    </Button>
  );
};

export default Connector;
